/**
 * InvoiceDialog Component
 * Displays invoice in a professional format with PDF download and print options
 *
 * @author SAUBHAGYA Development Team
 * @version 1.0.0
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Download, Printer, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { OrderDetails } from '@/types/order';
import orderInvoiceService from '@/services/commerce/orderInvoiceService';

// ============================================
// TYPES
// ============================================

export interface InvoiceDialogProps {
  orderId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format currency in Indian format (₹)
 */
function formatCurrency(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format date in dd-MM-yyyy format
 */
function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return 'Invalid Date';
  }
}

/**
 * Get payment status label and color
 */
function getPaymentStatusInfo(status: string): { label: string; className: string } {
  const statusMap: Record<string, { label: string; className: string }> = {
    PENDING: { label: 'Pending', className: 'text-yellow-600 bg-yellow-50' },
    PARTIAL: { label: 'Partially Paid', className: 'text-blue-600 bg-blue-50' },
    PAID: { label: 'Fully Paid', className: 'text-green-600 bg-green-50' },
    REFUNDED: { label: 'Refunded', className: 'text-purple-600 bg-purple-50' },
    FAILED: { label: 'Failed', className: 'text-red-600 bg-red-50' },
  };
  return statusMap[status] || { label: status, className: 'text-gray-600 bg-gray-50' };
}

// ============================================
// COMPONENT
// ============================================

export function InvoiceDialog({ orderId, isOpen, onClose }: InvoiceDialogProps): JSX.Element {
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ============================================
  // DATA FETCHING
  // ============================================

  useEffect(() => {
    if (isOpen && orderId) {
      loadInvoiceData();
    } else {
      // Reset state when dialog closes
      setOrder(null);
      setError(null);
    }
  }, [isOpen, orderId]);

  const loadInvoiceData = async () => {
    if (!orderId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await orderInvoiceService.getInvoiceData(orderId);
      setOrder(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load invoice data.';
      setError(errorMessage);
      console.error('[InvoiceDialog] Error loading invoice:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // EVENT HANDLERS
  // ============================================

  const handleDownloadPDF = () => {
    if (!order) return;

    try {
      orderInvoiceService.generateInvoicePDF(order);
      toast.success('Invoice Downloaded', {
        description: `Invoice ${order.orderNumber} has been downloaded successfully.`,
      });
    } catch (error) {
      console.error('[InvoiceDialog] Error downloading PDF:', error);
      toast.error('Download Failed', {
        description: error instanceof Error ? error.message : 'Failed to download invoice PDF.',
      });
    }
  };

  const handlePrint = () => {
    if (!order) return;

    try {
      orderInvoiceService.printInvoice(order);
      toast.success('Print Started', {
        description: 'Invoice has been sent to the printer.',
      });
    } catch (error) {
      console.error('[InvoiceDialog] Error printing:', error);
      toast.error('Print Failed', {
        description: error instanceof Error ? error.message : 'Failed to print invoice.',
      });
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Invoice</DialogTitle>
          <DialogDescription>
            {order ? `Order ${order.orderNumber}` : 'Loading invoice details...'}
          </DialogDescription>
        </DialogHeader>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mb-4" />
            <p className="text-gray-600">Loading invoice data...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Invoice Content */}
        {order && !isLoading && !error && (
          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="flex gap-3 justify-end print:hidden">
              <Button
                variant="outline"
                onClick={handleDownloadPDF}
                aria-label="Download PDF"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button
                variant="outline"
                onClick={handlePrint}
                aria-label="Print Invoice"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>

            {/* Company Header */}
            <div className="text-center border-b pb-4">
              <h1 className="text-2xl font-bold text-gray-900">SAUBHAGYA Biogas Systems</h1>
              <p className="text-sm text-gray-600">Industrial Area, Sector 15, Maharashtra, India - 400001</p>
              <p className="text-sm text-gray-600">GSTIN: 27AABCS1234F1Z5 | Phone: +91-9876543210 | Email: info@saubhagya.com</p>
            </div>

            {/* Invoice Title */}
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">TAX INVOICE</h2>
            </div>

            {/* Order Header */}
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded">
              <div>
                <p className="text-sm text-gray-600">Order Number</p>
                <p className="font-semibold">{order.orderNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Invoice Date</p>
                <p className="font-semibold">{formatDate(order.orderDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Order Date</p>
                <p className="font-semibold">{formatDate(order.orderDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="font-semibold">{order.orderStatus}</p>
              </div>
            </div>

            {/* Customer Information */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Bill To:</h3>
              <div className="bg-gray-50 p-4 rounded space-y-1">
                <p className="font-semibold text-gray-900">{order.customer.customerName}</p>
                <p className="text-sm text-gray-700">Email: {order.customer.email}</p>
                <p className="text-sm text-gray-700">Phone: {order.customer.phone}</p>
                {order.customer.gstNumber && (
                  <p className="text-sm text-gray-700">GST Number: {order.customer.gstNumber}</p>
                )}
              </div>
            </div>

            {/* Delivery Information */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Delivery Address:</h3>
              <div className="bg-gray-50 p-4 rounded space-y-1">
                <p className="text-sm text-gray-700">{order.delivery.address.addressLine1}</p>
                {order.delivery.address.addressLine2 && (
                  <p className="text-sm text-gray-700">{order.delivery.address.addressLine2}</p>
                )}
                <p className="text-sm text-gray-700">
                  {order.delivery.address.city}, {order.delivery.address.state} - {order.delivery.address.pincode}
                </p>
                <p className="text-sm text-gray-700">{order.delivery.address.country}</p>
                <p className="text-sm text-gray-700">
                  Contact: {order.delivery.contactPerson} ({order.delivery.contactPhone})
                </p>
                <p className="text-sm text-gray-700">
                  Expected Delivery: {formatDate(order.delivery.expectedDeliveryDate)}
                </p>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Line Items:</h3>
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2 font-semibold">S.No</th>
                      <th className="text-left p-2 font-semibold">Product</th>
                      <th className="text-left p-2 font-semibold">Quantity</th>
                      <th className="text-right p-2 font-semibold">Unit Price</th>
                      <th className="text-right p-2 font-semibold">Discount</th>
                      <th className="text-right p-2 font-semibold">Tax</th>
                      <th className="text-right p-2 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.lineItems.map((item, index) => (
                      <tr key={item.id} className="border-t">
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2">
                          <div>
                            <p className="font-medium">{item.productName}</p>
                            {item.description && (
                              <p className="text-xs text-gray-600">{item.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-2">
                          {item.quantity} {item.quantityUnit}
                        </td>
                        <td className="p-2 text-right">{formatCurrency(item.unitPrice)}</td>
                        <td className="p-2 text-right">{formatCurrency(item.discountAmount)}</td>
                        <td className="p-2 text-right">{formatCurrency(item.totalTax)}</td>
                        <td className="p-2 text-right font-semibold">{formatCurrency(item.lineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tax Breakdown */}
            <div className="flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span className="font-semibold">{formatCurrency(order.gstBreakdown.subtotal)}</span>
                </div>
                {order.gstBreakdown.totalDiscount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Discount:</span>
                    <span className="font-semibold text-red-600">
                      - {formatCurrency(order.gstBreakdown.totalDiscount)}
                    </span>
                  </div>
                )}
                {order.gstBreakdown.cgstAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>CGST (9%):</span>
                    <span className="font-semibold">{formatCurrency(order.gstBreakdown.cgstAmount)}</span>
                  </div>
                )}
                {order.gstBreakdown.sgstAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>SGST (9%):</span>
                    <span className="font-semibold">{formatCurrency(order.gstBreakdown.sgstAmount)}</span>
                  </div>
                )}
                {order.gstBreakdown.igstAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>IGST (18%):</span>
                    <span className="font-semibold">{formatCurrency(order.gstBreakdown.igstAmount)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-sm font-bold">
                  <span>Total Tax:</span>
                  <span>{formatCurrency(order.gstBreakdown.totalTax)}</span>
                </div>
                <div className="flex justify-between bg-gray-100 p-3 rounded font-bold text-lg">
                  <span>Total Amount:</span>
                  <span>{formatCurrency(order.gstBreakdown.totalAmount)}</span>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Payment Information:</h3>
              <div className="bg-gray-50 p-4 rounded space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Total Amount:</span>
                  <span className="font-semibold">{formatCurrency(order.payment.totalAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Paid Amount:</span>
                  <span className="font-semibold text-green-600">{formatCurrency(order.payment.paidAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Balance Due:</span>
                  <span className="font-semibold text-red-600">{formatCurrency(order.payment.balanceDue)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Payment Status:</span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      getPaymentStatusInfo(order.payment.paymentStatus).className
                    }`}
                  >
                    {getPaymentStatusInfo(order.payment.paymentStatus).label}
                  </span>
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            {order.delivery.specialInstructions && (
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Special Instructions:</h3>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <p className="text-sm text-gray-700 italic">{order.delivery.specialInstructions}</p>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="text-center text-xs text-gray-500 border-t pt-4">
              <p>Terms & Conditions: This is a computer-generated invoice and does not require a signature.</p>
              <p>Payment should be made within the agreed terms. For any queries, please contact us.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default InvoiceDialog;
